import { useEffect, useMemo, useState } from "react";

import {
  TextField,
  Button,
  Checkbox,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { ConfirmationModal } from "./components";
import {
  getSchoolCandidateNotesUrl,
  isDetailsDeleteVisible,
  isDetailsEditVisible,
} from "./utils";
import { searchRequestMetadata } from "../../api/Request/RequestApi";
import {
  deleteSchoolCandidate,
  getSchoolCandidateById,
  saveSchoolCandidateInstitutionCountyAssignment,
  saveSchoolCandidateRemark,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import {
  AddressSelect,
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { useEnumCodeBooks } from "../../hooks/useEnumCodeBooks";
import { SaveResult, ValidationFailure } from "../../models";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { RequestMetadata, RequestMetadataSearch } from "../../models/Request";
import {
  SchoolCandidate,
  UpdateSchoolCandidateInstitutionCountyAssignment,
  SchoolCandidateInstitutionChangeType,
  SchoolCandidateStatus,
  getDefaultSchoolCandidate,
  SchoolCandidateRemarkType,
} from "../../models/SchoolCandidate";
import { ClaimType } from "../../models/User";
import {
  useGetAlternativeCodeReasonListQuery,
  useGetAlternativeCodeTypeListQuery,
  useGetCountyListForUserQuery,
  useGetInstitutionListForUserQuery,
  useGetSchoolCandidateAddReasonListQuery,
  useGetSchoolCandidateCancelReasonListQuery,
} from "../../queries/codeBookQuery";
import { useGetRemarksQuery } from "../../queries/remarkQuery";
import {
  schoolCandidateEditUrl,
  schoolCandidateInstitutionAdminEditUrl,
  schoolCandidateInstitutionAdminListRoute,
  schoolCandidateInstitutionReassignmentUrl,
} from "../../routes/routes";
import { resetRedirectURL } from "../../slices/applicationSlice";
import { FormatUtils } from "../../utils";
import { ADMIN_ROLES } from "./pages/components/constants";

export function SchoolCandidateDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { schoolCandidateStatusCodeBook: statuses, genderCodeBook: genders } =
    useEnumCodeBooks();

  const redirectURL = useTypedSelector(
    (state) => state.application.redirectURL
  );

  const defaultSchoolCandidate = getDefaultSchoolCandidate();
  const [isLoading, setIsLoading] = useState(true);
  const [schoolCandidate, setSchoolCandidate] = useState<SchoolCandidate>(
    defaultSchoolCandidate
  );
  const { data: counties } = useGetCountyListForUserQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const { data: schoolCandidateAlternativeCodeReasons } =
    useGetAlternativeCodeReasonListQuery();
  const { data: schoolCandidateAlternativeCodeTypes } =
    useGetAlternativeCodeTypeListQuery();
  const { data: schoolCandidateAddReasons } =
    useGetSchoolCandidateAddReasonListQuery();
  const { data: schoolCandidateCancelReasons } =
    useGetSchoolCandidateCancelReasonListQuery();
  const [isEditable, setIsEditable] = useState(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [requests, setRequests] = useState<RequestMetadata[]>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDeletedModal, setShowDeletedModal] = useState(false);

  const isAllowedToEdit = useMemo(() => {
    const result =
      isEditable &&
      !(requests && requests.length > 0) &&
      isDetailsEditVisible(auth);

    return result;
  }, [isEditable, requests, auth]);

  const isDetailsDeleteVisibleMemo = useMemo(() => {
    return isDetailsDeleteVisible(auth);
  }, [auth]);

  const isAllowedToDelete = useMemo(() => {
    return (
      isDetailsDeleteVisible(auth) &&
      !schoolCandidate.hasRequest &&
      !schoolCandidate.hasRequestDraft
    );
  }, [auth, schoolCandidate.hasRequest, schoolCandidate.hasRequestDraft]);

  const deleteButtonVisibility = (() => {
    if (isAllowedToDelete) {
      return "show";
    }

    if (isDetailsDeleteVisibleMemo) {
      return "disable";
    }

    return "hide";
  })();

  const { data: remarks = [] } = useGetRemarksQuery(+id);

  useEffect(() => {
    (async () => {
      const result = await getSchoolCandidateById(+id);
      if (result) {
        setSchoolCandidate(result);
        if (result.schoolYear && result.schoolYear.isCurrent) {
          const eventId =
            ImportantDateEventEnum.DeliveryOfInitialLists as number;
          const importantDate = result.schoolYear.importantDates.find(
            (val) => val.eventId === eventId
          );
          if (importantDate) {
            const exactlyNow = new Date(Date.now());
            const dateFrom = new Date(importantDate.dateTimeFrom);
            const dateTo = new Date(importantDate.dateTimeTo);
            if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
              setIsEditable(true);
            }
          }
        }
        setIsLoading(false);
      }
      const requestSearch: RequestMetadataSearch = {
        institutionIds: [],
        schoolCandidateId: +id,
      };
      const requests = await searchRequestMetadata(requestSearch);

      if (!requests?.results) return;

      setRequests(requests.results);
    })();
  }, [id]);
  const handleEditClicked = () => {
    const adminUrl = schoolCandidateInstitutionAdminListRoute;
    if (history.location.pathname.startsWith(adminUrl))
      history.push(schoolCandidateInstitutionAdminEditUrl(+id));
    else history.push(schoolCandidateEditUrl(+id));
  };
  const handleReassignmentClicked = () => {
    history.push(schoolCandidateInstitutionReassignmentUrl(+id));
  };
  const handleConfirmationRequiredClicked = () => {
    setShowConfirmationModal(true);
  };
  const handleConfirmationSubmit = async (note: string) => {
    const newUpdate: UpdateSchoolCandidateInstitutionCountyAssignment = {
      schoolCandidateId: +id,
      changeType: SchoolCandidateInstitutionChangeType.Confirmation,
    };

    const result = await saveSchoolCandidateInstitutionCountyAssignment(
      newUpdate
    );

    let resultRemark: SaveResult<string> | null = null;

    if (note) {
      resultRemark = await saveSchoolCandidateRemark(
        +id,
        SchoolCandidateRemarkType.CountyVerificationRemark,
        note
      );
    }

    if (result?.isValid && (!resultRemark || resultRemark?.isValid)) {
      history.goBack();
    } else {
      setErrors(result.validationFailures);
    }
  };

  const handleDeleteClicked = async () => {
    var result = await deleteSchoolCandidate(+id);
    if (result) setShowDeletedModal(true);
  };

  const goBack = () => {
    if (redirectURL) {
      dispatch(resetRedirectURL());
      history.push(redirectURL);
    } else history.goBack();
  };
  if (isLoading) return null;

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.schoolCandidateModel.schoolCandidateDetails")}
        showBack={"show"}
        showDelete={deleteButtonVisibility}
        showEdit={isAllowedToEdit ? "show" : "disable"}
        onClickBack={goBack}
        onClickEdit={handleEditClicked}
        onClickDelete={handleDeleteClicked}
        errors={errors}
        deleteModalMessages={{
          deleteModalTitle: t(
            "common.schoolCandidateModel.deleteSchoolCandidateModalTitle"
          ),
          deleteModalMessage: t(
            "common.schoolCandidateModel.deleteSchoolCandidateModalMessage"
          ),
        }}
      />
      <Modal
        type={"message"}
        confirmText={t("common.close")}
        open={showDeletedModal}
        onConfirm={goBack}
        onClose={goBack}
      >
        {t("common.schoolCandidateModel.deleteSuccessSchoolCandidateModal")}
      </Modal>
      <FormCard
        cardTitle={t(
          "common.schoolCandidateModel.schoolCandidatePersonalDetails"
        )}
        labelStatus={{
          color: FormatUtils.getSchoolCandidateStatusColor(
            schoolCandidate.statusId
          ),
          name:
            statuses.find((v) => v.id === schoolCandidate.statusId)?.name ?? "",
        }}
      >
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              value={schoolCandidate.oib}
              readonly
              plaintext
            />
          </Col>
          <Col md={2}></Col>
          <Col md={6}></Col>
        </Row>
        {schoolCandidate.alternativeCode &&
          schoolCandidate.schoolCandidateAlternativeCodeTypeId &&
          schoolCandidate.schoolCandidateAlternativeCodeReasonId && (
            <Row>
              <Col md={4}>
                <TextField
                  label={t("common.schoolCandidateModel.alternativeCode")}
                  value={schoolCandidate.alternativeCode}
                  readonly
                  plaintext
                />
              </Col>
              <Col md={4}>
                <TextField
                  label={t(
                    "common.schoolCandidateModel.schoolCandidateAlternativeCodeType"
                  )}
                  value={
                    schoolCandidateAlternativeCodeTypes?.find(
                      (v) =>
                        v.id ===
                        schoolCandidate.schoolCandidateAlternativeCodeTypeId
                    )?.name
                  }
                  readonly
                  plaintext
                />
              </Col>
              <Col md={4}>
                <TextField
                  label={t(
                    "common.schoolCandidateModel.schoolCandidateAlternativeCodeReason"
                  )}
                  value={
                    schoolCandidateAlternativeCodeReasons?.find(
                      (v) =>
                        v.id ===
                        schoolCandidate.schoolCandidateAlternativeCodeReasonId
                    )?.name
                  }
                  readonly
                  plaintext
                />
              </Col>
            </Row>
          )}
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              value={schoolCandidate.lastName}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              value={schoolCandidate.firstName}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.dateOfBirth")}
              value={hrDate(new Date(schoolCandidate.dateOfBirth))}
              readonly
              plaintext
            />
          </Col>
          <Col md={2}>
            <TextField
              label={t("common.schoolCandidateModel.schoolCandidateGender")}
              value={
                genders.find((v) => v.id === schoolCandidate.genderId)?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.fatherName")}
              value={schoolCandidate.fatherName}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.motherName")}
              value={schoolCandidate.motherName}
              readonly
              plaintext
            />
          </Col>
        </Row>
      </FormCard>

      <AddressSelect
        title={t("common.schoolCandidateModel.permanentAddress")}
        value={schoolCandidate.residence?.permanentAddress}
      >
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.permanentAddressNote")}
              value={schoolCandidate.permanentAddressNote ?? ""}
              readonly
              plaintext
            />
          </Col>
        </Row>
      </AddressSelect>

      <AddressSelect
        title={t("common.schoolCandidateModel.temporaryAddress")}
        value={schoolCandidate.residence?.temporaryAddress}
      />

      <FormCard cardTitle={t("common.schoolCandidateModel.otherDetails")}>
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.schoolCandidateAddReason")}
              value={
                schoolCandidateAddReasons?.find(
                  (v) => v.id === schoolCandidate.schoolCandidateAddReasonId
                )?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t(
                "common.schoolCandidateModel.schoolCandidateAddReasonOther"
              )}
              value={schoolCandidate.schoolCandidateAddReasonOther ?? ""}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t(
                "common.schoolCandidateModel.schoolCandidateCancelReason"
              )}
              value={
                schoolCandidateCancelReasons?.find(
                  (v) => v.id === schoolCandidate.schoolCandidateCancelReasonId
                )?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t(
                "common.schoolCandidateModel.schoolCandidateCancelReasonOther"
              )}
              value={schoolCandidate.schoolCandidateCancelReasonOther ?? ""}
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.uoguCounty")}
              value={
                counties?.find((v) => v.id === schoolCandidate.uoguCountyId)
                  ?.name
              }
              readonly
              plaintext
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("common.schoolCandidateModel.institutionList")}
              value={
                institutions?.find(
                  (v) => v.id === schoolCandidate.institutionId
                )?.name
              }
              readonly
              plaintext
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Checkbox
              label={t("common.schoolCandidateModel.isHandicapped")}
              checked={schoolCandidate.isHandicapped}
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.nationalMinoritySchool")}
              value={
                schoolCandidate.religiousOrMinorityInstitutionName ??
                t("common.no").toUpperCase()
              }
              readonly
              plaintext
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showCancel="hide"
        showSave="hide"
        showCustom
        hasSchoolAttendanceNote={{
          visible: true,
          redirectUrl: getSchoolCandidateNotesUrl(window.location.href, +id),
          outline: !!remarks.length,
        }}
      >
        {(ADMIN_ROLES.includes(auth.currentRole?.role || 0) ||
          auth.currentRole?.claimType === ClaimType.County) && (
          <Button
            outline
            onClick={handleReassignmentClicked}
            disabled={
              schoolCandidate.statusId !== SchoolCandidateStatus.confirmation ||
              !isEditable
            }
          >
            {t("common.schoolCandidateModel.reassignInstitution")}
          </Button>
        )}
        {(ADMIN_ROLES.includes(auth.currentRole?.role || 0) ||
          auth.currentRole?.claimType === ClaimType.County ||
          auth.currentRole?.claimType === ClaimType.Institution) && (
          <Button
            outline
            onClick={handleConfirmationRequiredClicked}
            disabled={
              schoolCandidate.statusId !== SchoolCandidateStatus.onSchoolList ||
              !isEditable
            }
          >
            {t("common.schoolCandidateModel.confirmationRequired")}
          </Button>
        )}
      </ToolbarEdit>
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmationSubmit}
      />
    </ContentArea>
  );
}
